// ─────────────────────────────────────────────────────────────────────────────
// V2CreditNotes — /dashboard/credit-notes in the v2 design.
// Endpoints are v1's: GET /api/credit-notes[?status], GET /next-number, POST /,
// PUT /:id/cancel, GET /:id/pdf, GET /api/invoices/client/:id, GET /api/products.
// Create body is identical to v1. Differences: totals shown for manual mode too,
// products load correctly (v1 expected an array but the API returns { products }),
// and a 0% product keeps 0% (v1's `gstRate || 18` turned it into 18%).
// ─────────────────────────────────────────────────────────────────────────────
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import usePageTitle from "../hooks/usePageTitle";
import userContext from "../context/user/userContext";
import API_BASE from "../apiConfig";
import { Glyph, Duo } from "./icons";
import { MenuButton, Money, Segmented, Sheet, Skel, inr } from "./primitives";
import { Combo, Field, LineItems, PageHead, newLine, useClients, useProducts } from "./kit";

const BASE = `${API_BASE}/api`;
const auth = () => ({ "auth-token": localStorage.getItem("token") });
const json = () => ({ "Content-Type": "application/json", ...auth() });
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—");
const clientOf = (n) => n.clientName || n.clientId?.clientName || "Client";
const REASONS = ["Goods returned", "Post-sale discount", "Rate difference", "Damaged in transit", "Invoice error"];

function cnTotals(lines, supplyType) {
  let subtotal = 0, cgst = 0, sgst = 0, igst = 0;
  lines.forEach((it) => {
    const sub = (parseFloat(it.price) || 0) * (parseFloat(it.qty) || 0);
    const r = parseFloat(it.gstRate || 0);
    subtotal += sub;
    if (supplyType === "intra") { cgst += (sub * (r / 2)) / 100; sgst += (sub * (r / 2)) / 100; }
    else igst += (sub * r) / 100;
  });
  const gst = cgst + sgst + igst;
  return { subtotal: +subtotal.toFixed(2), cgst: +cgst.toFixed(2), sgst: +sgst.toFixed(2), igst: +igst.toFixed(2), gst: +gst.toFixed(2), total: +(subtotal + gst).toFixed(2) };
}

// ══════════════════════════════════════════════════════════════════════════════
export default function V2CreditNotes({ showAlert }) {
  usePageTitle("Credit Notes");
  const { hasPermission } = useContext(userContext);
  const canWrite = hasPermission("invoices", "write");
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [creating, setCreating] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [cancelling, setCancelling] = useState(null);
  const [pdfBusy, setPdfBusy] = useState("");
  const newBtn = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${BASE}/credit-notes${status ? `?status=${status}` : ""}`, { headers: auth() });
      const d = r.ok ? await r.json() : [];
      setNotes(Array.isArray(d) ? d : []);
    } catch { setNotes([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [status]); // eslint-disable-line

  const shown = notes.filter((n) => {
    const s = q.trim().toLowerCase();
    return !s || `${n.cnNumber} ${clientOf(n)} ${n.invoiceNumber || ""} ${n.reason || ""}`.toLowerCase().includes(s);
  });
  const activeTotal = notes.filter((n) => n.status === "ACTIVE").reduce((s, n) => s + (n.grandTotal || 0), 0);

  const pdf = async (n) => {
    setPdfBusy(n._id);
    try {
      const r = await fetch(`${BASE}/credit-notes/${n._id}/pdf`, { headers: auth() });
      if (!r.ok) throw new Error();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(await r.blob());
      a.download = `Credit-Note-${n.cnNumber}.pdf`; a.click();
    } catch { showAlert?.("Couldn't generate the PDF", "danger"); }
    finally { setPdfBusy(""); }
  };

  const menu = (n) => [
    { label: "View details", glyph: "invoice", onSelect: () => setViewing(n) },
    { label: pdfBusy === n._id ? "Preparing PDF…" : "Download PDF", glyph: "download", onSelect: () => pdf(n) },
    ...(canWrite && n.status === "ACTIVE" ? ["divider", { label: "Cancel credit note", glyph: "close", danger: true, onSelect: () => setCancelling(n) }] : []),
  ];

  return (
    <div className="v2p v2cn">
      <PageHead eyebrow="Receivables" title="Credit notes"
        sub={loading ? "Loading…" : `${inr(activeTotal)} credited across ${notes.filter((n) => n.status === "ACTIVE").length} active note${notes.filter((n) => n.status === "ACTIVE").length === 1 ? "" : "s"}`}
        actions={canWrite && <button ref={newBtn} type="button" className="v2-btn v2-btn--primary" onClick={() => setCreating(true)}><Glyph name="plus" size={17} stroke={2.2} /> New credit note</button>} />

      <div className="v2i-toolbar">
        <Segmented ariaLabel="Status" value={status} onChange={setStatus}
          options={[{ value: "", label: "All" }, { value: "ACTIVE", label: "Active" }, { value: "CANCELLED", label: "Cancelled" }]} />
        <div className="v2-field--search v2i-search">
          <Glyph name="search" size={17} />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Credit note no., client, invoice, reason" aria-label="Search credit notes" />
        </div>
      </div>

      <section className="v2-card v2i-card">
        {loading ? <div className="v2-list" style={{ padding: 8 }}>{[0, 1, 2, 3].map((i) => <Skel key={i} h={58} r={16} />)}</div>
          : shown.length === 0 ? (
            <div className="v2c-empty"><Duo name="note" size={44} /><strong>{q ? "No matches" : "No credit notes"}</strong>
              <span>Issue a credit note when goods come back or a price needs correcting — the client's ledger is credited automatically.</span>
              {canWrite && !q && <button type="button" className="v2-btn v2-btn--primary v2-btn--sm" onClick={() => setCreating(true)}>New credit note</button>}
            </div>
          ) : (
            <div className="v2i-rows">
              {shown.map((n) => (
                <div key={n._id} className={`v2cn-row${n.status === "CANCELLED" ? " is-void" : ""}`}>
                  <button type="button" className="v2cn-open" onClick={() => setViewing(n)}>
                    <Duo name="note" size={30} />
                    <span className="v2-row-main">
                      <span className="v2i-no">{n.cnNumber}{n.invoiceNumber && <span className="v2cn-link">↳ {n.invoiceNumber}</span>}</span>
                      <span className="v2-row-sub">{clientOf(n)} · {fmtDate(n.cnDate)}{n.reason ? ` · ${n.reason}` : ""}</span>
                    </span>
                    <span className="v2cn-end">
                      <span className="v2i-total">{inr(n.grandTotal, { decimals: 2 })}</span>
                      {n.status === "CANCELLED"
                        ? <span className="v2-status v2-status--bad"><Glyph name="close" size={12} stroke={2.4} /> Cancelled</span>
                        : <span className="v2-status v2-status--good"><Glyph name="check" size={13} stroke={2.2} /> Credited</span>}
                    </span>
                  </button>
                  <MenuButton items={menu(n)} className="v2-icon-btn v2i-more" label={`Actions for ${n.cnNumber}`} width={230} />
                </div>
              ))}
            </div>
          )}
      </section>

      <CreateSheet open={creating} originRef={newBtn} onClose={() => setCreating(false)}
        onSaved={(d) => { setCreating(false); showAlert?.(`Credit note ${d?.cnNumber || ""} issued`, "success"); load(); }} />
      <ViewSheet note={viewing} onClose={() => setViewing(null)} onPdf={pdf} busy={pdfBusy} />
      <CancelSheet note={cancelling} onClose={() => setCancelling(null)}
        onDone={(n) => { setCancelling(null); showAlert?.(`${n.cnNumber} cancelled`, "success"); load(); }} />
    </div>
  );
}

// ── Create ───────────────────────────────────────────────────────────────────
function CreateSheet({ open, originRef, onClose, onSaved }) {
  const today = new Date().toISOString().split("T")[0];
  const { clients } = useClients();
  const { products, loading: pLoading } = useProducts();
  const [cnNumber, setCnNumber] = useState("");
  const [cnDate, setCnDate] = useState(today);
  const [clientId, setClientId] = useState("");
  const [supplyType, setSupply] = useState("intra");
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [invoices, setInvoices] = useState([]);
  const [invLoading, setInvLoading] = useState(false);
  const [invoiceId, setInvoiceId] = useState("");
  const [sel, setSel] = useState({});
  const [lines, setLines] = useState(() => [newLine({ gstRate: 18 })]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const was = useRef(false);

  useEffect(() => {
    if (open && !was.current) {
      setCnDate(today); setClientId(""); setSupply("intra"); setReason(""); setNotes(""); setInvoices([]);
      setInvoiceId(""); setSel({}); setLines([newLine({ gstRate: 18 })]); setErr("");
      fetch(`${BASE}/credit-notes/next-number`, { headers: auth() }).then((r) => r.json()).then((d) => setCnNumber(d.cnNumber || "")).catch(() => {});
    }
    was.current = open;
  }, [open]); // eslint-disable-line

  const onClient = async (id) => {
    setClientId(id); setInvoiceId(""); setSel({}); setInvoices([]);
    if (!id) return;
    setInvLoading(true);
    try {
      const r = await fetch(`${BASE}/invoices/client/${id}`, { headers: auth() });
      const d = await r.json();
      setInvoices(Array.isArray(d) ? d : []);
    } catch { setInvoices([]); }
    finally { setInvLoading(false); }
  };

  const invoice = invoices.find((i) => i._id === invoiceId) || null;
  const onInvoice = (id) => {
    setInvoiceId(id);
    const inv = invoices.find((i) => i._id === id);
    if (!inv) { setSel({}); return; }
    setSupply(inv.items?.[0]?.gstType === "IGST" ? "inter" : "intra"); // v1 rule
    const s = {};
    (inv.items || []).forEach((it, i) => { s[i] = { checked: true, qty: it.qty || 1 }; });
    setSel(s);
  };

  // Items to send — linked invoice checklist or manual lines (v1 shapes)
  const finalItems = invoice
    ? (invoice.items || []).map((it, i) => ({ it, s: sel[i] })).filter(({ s }) => s?.checked && parseFloat(s.qty) > 0).map(({ it, s }) => {
      const qty = parseFloat(s.qty) || 0; const price = parseFloat(it.price) || 0;
      return { productId: it.productId || null, name: it.name || "", hsnCode: it.hsnCode || "", price, qty, gstRate: parseFloat(it.gstRate || 0), unit: it.unit || "", amount: parseFloat((price * qty).toFixed(2)) };
    })
    : lines.filter((l) => l.productId || l.name?.trim()).map((l) => ({
      productId: l.productId || null, name: l.name, hsnCode: l.hsnCode || "", qty: parseFloat(l.qty) || 0,
      price: parseFloat(l.price) || 0, gstRate: parseFloat(l.gstRate || 0), unit: l.unit || "",
    }));
  const overQty = invoice && Object.entries(sel).some(([i, s]) => s.checked && parseFloat(s.qty) > (invoice.items[+i]?.qty || 0));
  const t = useMemo(() => cnTotals(finalItems, supplyType), [JSON.stringify(finalItems), supplyType]); // eslint-disable-line

  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (!clientId) { setErr("Choose a client."); return; }
    if (!finalItems.length) { setErr("Include at least one item."); return; }
    if (overQty) { setErr("A quantity is more than was invoiced."); return; }
    const noName = finalItems.findIndex((it) => !it.name?.trim());
    if (noName !== -1) { setErr(`Item ${noName + 1} has no name.`); return; }
    setBusy(true);
    try {
      const r = await fetch(`${BASE}/credit-notes`, {
        method: "POST", headers: json(),
        body: JSON.stringify({ cnNumber, cnDate, clientId, supplyType, reason, notes, invoiceId: invoiceId || null, invoiceNumber: invoice?.invoiceNumber || "", items: finalItems.map((it) => ({ ...it, productId: it.productId || null })) }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setErr(d.error || d.message || "Couldn't create the credit note."); return; }
      onSaved(d);
    } catch { setErr("Network error — nothing was saved."); }
    finally { setBusy(false); }
  };

  const clientItems = clients.map((c) => ({ id: c._id, label: c.clientName, sub: c.clientGSTIN || "" }));
  const invItems = [{ id: "", label: "Not linked", sub: "Enter items manually" }, ...invoices.map((i) => ({
    id: i._id, label: i.invoiceNumber, sub: `${fmtDate(i.invoiceDate)} · ${inr(i.grandTotal, { decimals: 2 })}`,
  }))];

  return (
    <Sheet open={open} onClose={onClose} originRef={originRef} width={760} title="New credit note" subtitle={cnNumber ? `${cnNumber} · credits the client's ledger` : "Credits the client's ledger"}
      footer={<>
        <span className="v2cn-foot-total">Credit <b>{inr(t.total, { decimals: 2 })}</b></span>
        <button type="button" className="v2-btn" onClick={onClose}>Cancel</button>
        <button type="submit" form="v2cn-form" className="v2-btn v2-btn--primary" disabled={busy}>{busy ? "Issuing…" : "Issue credit note"}</button>
      </>}>
      <form id="v2cn-form" className="v2-form" onSubmit={submit}>
        <div className="v2-form-row">
          <Field label="Client"><Combo items={clientItems} value={clientId} onChange={onClient} placeholder="Choose a client" searchPlaceholder="Search clients" ariaLabel="Client" /></Field>
          <Field label="Against invoice" optional hint={invLoading ? "Loading invoices…" : clientId ? `${invoices.length} invoice${invoices.length === 1 ? "" : "s"} for this client` : "Choose a client first"}>
            <Combo items={invItems} value={invoiceId} onChange={onInvoice} disabled={!clientId} placeholder="Not linked" searchPlaceholder="Search invoice number" ariaLabel="Invoice" />
          </Field>
        </div>
        <div className="v2-form-row">
          <Field label="Credit note no."><input className="v2-input v2-mono" value={cnNumber} onChange={(e) => setCnNumber(e.target.value)} /></Field>
          <Field label="Date"><input className="v2-input" type="date" value={cnDate} onChange={(e) => setCnDate(e.target.value)} /></Field>
        </div>
        <Field label="Supply" hint={invoice ? "Taken from the linked invoice" : undefined}>
          <Segmented ariaLabel="Supply type" value={supplyType} onChange={setSupply}
            options={[{ value: "intra", label: "Intra-state · CGST+SGST" }, { value: "inter", label: "Inter-state · IGST" }]} />
        </Field>

        <div className="v2c-section-title" style={{ margin: "6px 0 0 4px" }}>{invoice ? `Items from ${invoice.invoiceNumber}` : "Items"}</div>
        {invoice ? (
          <div className="v2-inset v2l-pick">
            {(invoice.items || []).map((it, i) => {
              const s = sel[i] || { checked: false, qty: it.qty };
              const over = s.checked && parseFloat(s.qty) > (it.qty || 0);
              return (
                <div key={i} className={`v2-inset-row v2l-pick-row${s.checked ? " is-on" : ""}`}>
                  <label className="v2i-check"><input type="checkbox" checked={!!s.checked} onChange={() => setSel((p) => ({ ...p, [i]: { ...s, checked: !s.checked } }))} /><span><Glyph name="check" size={12} stroke={3} /></span></label>
                  <span className="v2-row-main">
                    <span className="v2-row-title v2-wrap">{it.name}</span>
                    <span className="v2-row-sub">{inr(it.price, { decimals: 2 })} · GST {it.gstRate}% · invoiced {it.qty} {it.unit || ""}</span>
                    {over && <span className="v2-field-err">Max {it.qty}</span>}
                  </span>
                  <span className="v2cn-qty">
                    <button type="button" className="v2-icon-btn" aria-label="Less" disabled={!s.checked || parseFloat(s.qty) <= 1} onClick={() => setSel((p) => ({ ...p, [i]: { ...s, qty: Math.max(1, (parseFloat(s.qty) || 1) - 1) } }))}>−</button>
                    <input className="v2-input" type="number" min="1" max={it.qty} value={s.qty} disabled={!s.checked}
                      onChange={(e) => setSel((p) => ({ ...p, [i]: { ...s, qty: e.target.value } }))} aria-label={`Return quantity for ${it.name}`} />
                    <button type="button" className="v2-icon-btn" aria-label="More" disabled={!s.checked || parseFloat(s.qty) >= it.qty} onClick={() => setSel((p) => ({ ...p, [i]: { ...s, qty: Math.min(it.qty, (parseFloat(s.qty) || 0) + 1) } }))}>+</button>
                  </span>
                </div>
              );
            })}
          </div>
        ) : (
          <LineItems items={lines} onChange={setLines} products={products} productsLoading={pLoading} defaultGst={18} amountWithGst compact />
        )}

        <div className="v2-inset v2cn-totals">
          <div className="v2-inset-row v2-inset-row--static"><span>Subtotal</span><b>{inr(t.subtotal, { decimals: 2 })}</b></div>
          {supplyType === "intra" ? (<>
            <div className="v2-inset-row v2-inset-row--static"><span>CGST</span><b>{inr(t.cgst, { decimals: 2 })}</b></div>
            <div className="v2-inset-row v2-inset-row--static"><span>SGST</span><b>{inr(t.sgst, { decimals: 2 })}</b></div>
          </>) : <div className="v2-inset-row v2-inset-row--static"><span>IGST</span><b>{inr(t.igst, { decimals: 2 })}</b></div>}
          <div className="v2-inset-row v2-inset-row--static v2pub-grand"><span>Credit total</span><b>{inr(t.total, { decimals: 2 })}</b></div>
        </div>

        <Field label="Reason" optional>
          <div className="v2i-presets">{REASONS.map((r) => <button key={r} type="button" className={`v2-chip-btn v2i-preset${reason === r ? " is-on" : ""}`} onClick={() => setReason(reason === r ? "" : r)}>{r}</button>)}</div>
          <input className="v2-input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Or describe the reason" style={{ marginTop: 8 }} />
        </Field>
        <Field label="Notes" optional><textarea className="v2-input v2-textarea" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} /></Field>
        {err && <div className="v2-banner v2-banner--bad" role="alert"><Glyph name="alert" size={16} /> {err}</div>}
      </form>
    </Sheet>
  );
}

function ViewSheet({ note, onClose, onPdf, busy }) {
  const keep = useRef(note);
  if (note) keep.current = note;
  const n = keep.current;
  if (!n) return null;
  return (
    <Sheet open={!!note} onClose={onClose} width={560} title={n.cnNumber} subtitle={`${clientOf(n)} · ${fmtDate(n.cnDate)}`}
      footer={<><button type="button" className="v2-btn" onClick={onClose}>Close</button>
        <button type="button" className="v2-btn v2-btn--primary" disabled={busy === n._id} onClick={() => onPdf(n)}>{busy === n._id ? "Preparing…" : "Download PDF"}</button></>}>
      <div className="v2pub-receipt">
        <div className="v2pub-receipt-top">
          <div><span className="v2-label">Credited</span><div className="v2pub-receipt-amt"><Money value={n.grandTotal} /></div></div>
          {n.status === "CANCELLED" ? <span className="v2-status v2-status--bad">Cancelled</span> : <span className="v2-status v2-status--good"><Glyph name="check" size={13} stroke={2.2} /> Active</span>}
        </div>
        <dl className="v2pub-meta">
          <div><dt>Against</dt><dd>{n.invoiceNumber || "—"}</dd></div>
          <div><dt>Supply</dt><dd>{n.supplyType === "inter" ? "Inter-state" : "Intra-state"}</dd></div>
          <div><dt>Reason</dt><dd>{n.reason || "—"}</dd></div>
          <div><dt>Items</dt><dd>{(n.items || []).length}</dd></div>
        </dl>
        {(n.items || []).length > 0 && (
          <div className="v2-inset">
            {n.items.map((it, i) => (
              <div key={i} className="v2-inset-row v2-inset-row--static">
                <span className="v2-row-main"><span className="v2-row-title v2-wrap">{it.name}</span><span className="v2-row-sub">{it.qty} × {inr(it.price, { decimals: 2 })} · GST {it.gstRate}%</span></span>
                <b>{inr((it.price || 0) * (it.qty || 0), { decimals: 2 })}</b>
              </div>
            ))}
          </div>
        )}
        {n.notes && <p className="v2-row-sub">{n.notes}</p>}
      </div>
    </Sheet>
  );
}

function CancelSheet({ note, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const keep = useRef(note);
  if (note) keep.current = note;
  const n = keep.current;
  useEffect(() => { if (note) setErr(""); }, [note]);
  if (!n) return null;
  const go = async () => {
    setBusy(true); setErr("");
    try {
      const r = await fetch(`${BASE}/credit-notes/${n._id}/cancel`, { method: "PUT", headers: auth() });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || d.message || "Couldn't cancel");
      onDone(n);
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };
  return (
    <Sheet open={!!note} onClose={onClose} width={440} title={`Cancel ${n.cnNumber}?`}
      footer={<><button type="button" className="v2-btn" onClick={onClose}>Keep</button>
        <button type="button" className="v2-btn v2c-btn-danger" disabled={busy} onClick={go}>{busy ? "Cancelling…" : "Cancel credit note"}</button></>}>
      <p className="v2c-remove-text">The {inr(n.grandTotal, { decimals: 2 })} credit on {clientOf(n)}'s ledger is reversed. This can't be undone.</p>
      {err && <div className="v2-banner v2-banner--bad"><Glyph name="alert" size={16} /> {err}</div>}
    </Sheet>
  );
}
